export type Language = (typeof languages)[number];
export const languages = [
  'ar',
  'bg',
  'de',
  'en-gb',
  'en-us',
  'es-es',
  'es-us',
  'fr',
  'fr-ca',
  'he',
  'hi',
  'hr',
  'ht',
  'id',
  'it',
  'ja',
  'ko',
  'nl',
  'pt-BR',
  'pt-PT',
  'ru',
  'ukr',
  'zh-cn',
  'zh-hk',
  'zh-tw',
] as const;

export const languageLabels: Record<Language, string> = {
  'en-us': 'English (US)',
  ar: 'عربي (Arabic)',
  bg: 'български (Bulgarian)',
  de: 'Deutsch (German)',
  'en-gb': 'English (UK)',
  'es-es': 'Español (Spain)',
  'es-us': 'Español (Spanish - Latin America)',
  fr: 'Français (French)',
  'fr-ca': 'Français (Canadian French)',
  he: 'עִברִית (Hebrew)',
  hi: 'हिंदी (Hindi)',
  hr: 'Croatian (hrvatski)',
  ht: 'Haitian Creole (kreyòl ayisyen)',
  id: 'Bahasa Indonesia (Indonesian)',
  it: 'Italiano (Italian)',
  ja: '日本語 (Japanese)',
  ko: '한국어 (Korean)',
  nl: 'Dutch (Nederlands)',
  'pt-BR': 'Português (Brazilian Portuguese)',
  'pt-PT': 'Português (European Portuguese)',
  ru: 'Русский (Russian)',
  ukr: 'Українська (Ukrainian)',
  'zh-cn': '普通话 (Mandarin)',
  'zh-hk': '廣東話 (Cantonese)',
  'zh-tw': '繁體中文（台灣） (Traditional Chinese - Taiwan)',
};

export const DEFAULT_LOCALE = 'en-us' as const;

/**
 * Extra translator guidance per locale, read via `localeGuidanceFor(lang)` in the
 * translate-script prompts. Most locales need none beyond their ISO code and label;
 * a locale PR adds an entry here only when the bare code mistranslates (e.g. zh-tw needs
 * Taiwan vocabulary rules to keep Hong Kong and Mainland forms out).
 */
export const LOCALE_GUIDANCE: Partial<Record<Language, string>> = {
  'zh-tw': `
  ⚠️ LOCALE: zh-tw — Traditional Chinese as used in Taiwan (Taiwanese Mandarin written form).
  - Use Traditional Chinese characters (繁體), NEVER Simplified.
  - Use Taiwan vocabulary and phrasing, NOT Hong Kong / Cantonese written Chinese.
  - Address the reader as 您 (not 你) in teacher and admin UI; use 你 only where the
    English source is explicitly casual or student-directed.
  - The MagicStudent product "room" is 房間 (not 教室, which is a physical classroom,
    and not 學習空間); use 教室 only where the English source means a real classroom.
  - Always prefer the Taiwan form (verified Taiwan usage first, rejected form in parentheses):
    Core software terms: 軟體 (not 軟件), 程式 (not 程序) for software programs,
    伺服器 (not 服务器) for server, 檔案 (not 文件) for computer files, 資料 (not 數據) for data,
    副檔名 (not 擴展名) for file extensions, 網路 (not 網絡), 資訊 (not 信息),
    網際網路 (not 互聯網), 使用者 (not 用戶, but keep 用戶端 for "client").
    Actions: 新增 (not 添加) for add, 儲存 (not 保存) for save, 開啟 (not 打開) for open,
    重新整理 (not 刷新) for refresh, 載入 (not 加載) for load, 匯入 (not 導入) for import,
    搜尋 (not 查找/搜索) for search, 追蹤 (not 跟蹤) for track, 取得 (not 獲取) for fetch,
    設定 (not 設置), 重設 (not 重置) for reset, 停用 (not 禁用) for disable,
    預設 (not 默認) for default, 自訂 (not 定制) for customize,
    登入 (not 登錄) for sign in, 執行 (not 運行) for run/launch tools.
    Communication & content: 訊息 (not 消息) for messages, 回饋 (not 反饋) for feedback,
    討論串 or 對話 (not 線程) for chat threads, 影片 (not 視頻) for video,
    音訊 (not 音頻) for audio, 文字 (not 文本) for text, 字元 (not 字符) for characters,
    投影片 (not 幻燈片) for slides, Google 簡報 for Google Slides,
    圖庫 (not 畫廊) for a media gallery, 螢幕截圖 for screenshots,
    螢幕 (not 屏幕) for screen, 彈出視窗 (not 弹出窗口) for popup windows.
    Education & product: 學區 (not 區域/地區) for school district, 學校 (not 網站)
    for a school site or campus, 學習單 (not 工作紙) for worksheets,
    對應 or 符合 (not 對齊) for standards alignment, 實務 (not 實踐) for practice,
    評分標準 (not 评分标准) for rubrics, 瞭解 (not 了解) for learn more/understand,
    透過 (not 通過) for "via/through a service", 裡 (not 裏).
    Support & system: 支援 (not 支持) for tech support, 工單 (not 票據) for support
    tickets, 工作階段 (not 會話) for sessions, 存取 (not 訪問) for system/data access,
    智慧 (not 智能) for smart/AI features, 品質 (not 質量) for quality,
    清單 (not 列表) for lists, 個人化 (not 個性化) for personalize,
    導覽 (not 導航) for site navigation, 在地 or 本機 (not 本地) for local.
  - Do NOT use colloquial Cantonese grammar (sentence-final particles, Cantonese-only
    characters); the vocabulary line above covers word choice.
  `,
  ko: `
  ⚠️ LOCALE: ko — Korean.
  - An automated moderation "flag" or "flagged" is 플래그 (e.g. 플래그됨, 긴급 플래그), never 신고,
    which this app reserves for a report a person files.
  `,
};

export const isLanguage = (language: string): language is Language => languages.includes(language as Language);

/** The extra translator guidance for a locale code, or '' for locales (and typos) without an entry. */
export const localeGuidanceFor = (code: string): string => (isLanguage(code) ? (LOCALE_GUIDANCE[code] ?? '') : '');

/** The supported language a locale names, or the default locale for anything else (including a missing one). */
export const toLanguage = (locale: string | null | undefined): Language => (locale && isLanguage(locale) ? locale : DEFAULT_LOCALE);
